import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  CUSTOM_FAQ_PATHS,
  DOCUMENT_SUBGROUPS,
  ROUTE_FAQ_OVERRIDES,
  ROUTE_GROUPS,
  ROUTE_LABELS,
} from '../data/routeMetadata';

const titleize = (value) => value
  .split('-')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const getRouteLabel = (pathname) => {
  if (pathname === '/blog') {
    return 'the paisapyara blog';
  }

  if (pathname.startsWith('/blog/')) {
    return 'this finance article';
  }

  if (ROUTE_LABELS[pathname]) {
    return ROUTE_LABELS[pathname];
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'paisapyara';
  }

  return titleize(segments[segments.length - 1]);
};

const getRouteCategory = (pathname) => {
  if (ROUTE_GROUPS.loans.includes(pathname)) return 'loans';
  if (ROUTE_GROUPS.investing.includes(pathname)) return 'investments';
  if (ROUTE_GROUPS.savingsSchemes.includes(pathname)) return 'governmentSchemes';
  if (ROUTE_GROUPS.retirement.includes(pathname)) return 'retirement';
  if (ROUTE_GROUPS.tax.includes(pathname)) return 'tax';
  if (ROUTE_GROUPS.realEstate.includes(pathname)) return 'realEstate';
  if (ROUTE_GROUPS.business.includes(pathname)) return 'business';
  if (ROUTE_GROUPS.bills.includes(pathname)) return 'bills';
  if (ROUTE_GROUPS.interest.includes(pathname)) return 'interest';
  return 'general';
};

const getDocumentSubgroup = (pathname) => {
  if (DOCUMENT_SUBGROUPS.receipts.includes(pathname)) return 'receipts';
  if (DOCUMENT_SUBGROUPS.payroll.includes(pathname)) return 'payroll';
  if (DOCUMENT_SUBGROUPS.hospitality.includes(pathname)) return 'hospitality';
  if (DOCUMENT_SUBGROUPS.quotations.includes(pathname)) return 'quotations';
  return 'general';
};

const buildCategoryFaqs = (pathname, routeLabel) => {
  const category = getRouteCategory(pathname);

  switch (category) {
    case 'loans':
      return [
        {
          question: `What can I check with ${routeLabel}?`,
          answer: `${routeLabel} helps you compare loan affordability, repayment impact, or eligibility assumptions before you apply or prepay.`,
        },
        {
          question: 'Will changing tenure or rate affect the result?',
          answer: 'Yes. Even small changes in interest rate, tenure, or repayment amount can materially change EMI, eligibility, or total interest paid.',
        },
        {
          question: 'Can I use this tool before applying for a loan?',
          answer: 'Yes. It is useful for planning and comparison before speaking to a bank or lender.',
        },
        {
          question: 'Should I still verify final loan terms with the lender?',
          answer: 'Yes. Actual processing fees, insurance, floating rates, and lender policies can change your real repayment outcome.',
        },
      ];
    case 'investments':
      return [
        {
          question: `What does ${routeLabel} help me estimate?`,
          answer: `${routeLabel} helps you estimate growth, returns, or investment outcomes using your own contribution, rate, and time assumptions.`,
        },
        {
          question: 'Are these investment returns guaranteed?',
          answer: 'No. The results are estimates based on the numbers you enter and should not be treated as guaranteed returns.',
        },
        {
          question: 'Why does time horizon matter so much?',
          answer: 'Longer time periods give compounding more time to work, which can significantly affect the final result.',
        },
        {
          question: 'Can I compare multiple scenarios with this tool?',
          answer: 'Yes. Change one variable at a time, such as return rate, amount, or duration, to compare outcomes more clearly.',
        },
      ];
    case 'governmentSchemes':
      return [
        {
          question: `Who is ${routeLabel} useful for?`,
          answer: `${routeLabel} is useful for users evaluating government-backed or regulated saving schemes for long-term planning.`,
        },
        {
          question: 'Can scheme rules change over time?',
          answer: 'Yes. Interest rates, eligibility rules, and deposit limits can change, so you should verify the latest official guidelines before investing.',
        },
        {
          question: 'Is this tool suitable for planning maturity value?',
          answer: 'Yes. It is helpful for estimating maturity amount and understanding how the scheme can fit into your savings plan.',
        },
        {
          question: 'Should I verify final numbers from official sources?',
          answer: 'Yes. Use official notifications, bank/post office documents, or scheme circulars for final decision-making.',
        },
      ];
    case 'retirement':
      return [
        {
          question: `How can ${routeLabel} help with retirement planning?`,
          answer: `${routeLabel} helps you estimate corpus needs, future income, retirement savings growth, or withdrawal sustainability based on your assumptions.`,
        },
        {
          question: 'Should inflation be considered in retirement planning?',
          answer: 'Yes. Inflation can materially reduce purchasing power over long periods, so retirement planning should always consider future living costs.',
        },
        {
          question: 'Can I test different contribution or return assumptions?',
          answer: 'Yes. Adjust the inputs to compare how savings rate, expected return, or retirement duration changes your outcome.',
        },
        {
          question: 'Is this a substitute for full retirement advice?',
          answer: 'No. It is a planning aid, but final retirement decisions should consider taxes, healthcare, risk tolerance, and cash flow needs in detail.',
        },
      ];
    case 'tax':
      return [
        {
          question: `What is ${routeLabel} used for?`,
          answer: `${routeLabel} helps you estimate a tax-related amount such as exemption, deduction, withholding, or gain-based tax using your inputs.`,
        },
        {
          question: 'Are the results suitable for tax planning?',
          answer: 'Yes. The tool is useful for tax planning and estimation, especially before filing or making year-end decisions.',
        },
        {
          question: 'Can tax rules change by financial year?',
          answer: 'Yes. Tax rules, thresholds, and slab rates may change, so always cross-check with the applicable financial year and official guidance.',
        },
        {
          question: 'Should I rely only on this tool for compliance?',
          answer: 'No. Use it for estimation, but confirm final compliance numbers with official documents or a qualified tax professional.',
        },
      ];
    case 'realEstate':
      return [
        {
          question: `What can ${routeLabel} help me evaluate?`,
          answer: `${routeLabel} helps you estimate property-related cost, yield, or buy-versus-rent comparisons with structured assumptions.`,
        },
        {
          question: 'Should I include hidden property costs in my planning?',
          answer: 'Yes. Registration, maintenance, taxes, legal fees, vacancy, and furnishing can materially affect your real outcome.',
        },
        {
          question: 'Are these property results exact?',
          answer: 'No. They are estimates based on your inputs and should be combined with local market research and actual lender or government charges.',
        },
        {
          question: 'Can I compare different assumptions here?',
          answer: 'Yes. Adjust rent, yield, property value, or cost assumptions to compare multiple scenarios before making a decision.',
        },
      ];
    case 'business':
      return [
        {
          question: `How is ${routeLabel} useful for business or salary planning?`,
          answer: `${routeLabel} helps you estimate pricing, cost structure, profitability, salary breakdown, or professional documents more quickly and consistently.`,
        },
        {
          question: 'Can I use this tool for planning and documentation?',
          answer: 'Yes. These tools are useful for quick internal planning, comparisons, and generating clean document outputs.',
        },
        {
          question: 'Should I still verify business numbers manually?',
          answer: 'Yes. Always verify tax rules, pricing assumptions, and official document requirements before final use.',
        },
        {
          question: 'Can I export results from these tools?',
          answer: 'Many paisapyara tools include PDF export or structured outputs to help you save, print, or share results.',
        },
      ];
    case 'bills':
      switch (getDocumentSubgroup(pathname)) {
        case 'receipts':
          return [
            {
              question: `What is ${routeLabel} used for?`,
              answer: `${routeLabel} helps you create a structured receipt with the key party, payment, and reference details needed for record-keeping or sharing.`,
            },
            {
              question: 'Can I generate a printable receipt from this page?',
              answer: 'Yes. These receipt tools are designed to produce readable outputs that can typically be printed or saved as PDF.',
            },
            {
              question: 'Should I verify names, dates, and amounts before sharing?',
              answer: 'Yes. Always check the receipt details carefully before issuing or printing the final version.',
            },
            {
              question: 'Are these receipt entries stored permanently online?',
              answer: 'No. paisapyara receipt generators primarily run in your browser, which helps keep entered information private during use.',
            },
          ];
        case 'payroll':
          return [
            {
              question: `How can ${routeLabel} help with payroll records?`,
              answer: `${routeLabel} helps you organize salary, attendance, deductions, or payout details into a clean document for internal or personal record use.`,
            },
            {
              question: 'Can I use this page for printable salary or wage documents?',
              answer: 'Yes. These tools are suitable for generating readable salary-style documents and often support PDF export.',
            },
            {
              question: 'Should statutory deductions be checked separately?',
              answer: 'Yes. You should verify deductions, tax rules, and employment compliance before using generated documents officially.',
            },
            {
              question: 'Is employee or helper data stored on your server?',
              answer: 'No. The generator primarily runs in your browser, which helps keep entered information private during use.',
            },
          ];
        case 'hospitality':
          return [
            {
              question: `What kind of bill can I create with ${routeLabel}?`,
              answer: `${routeLabel} helps generate a practical service or transaction bill with customer details, line items, totals, and tax fields where needed.`,
            },
            {
              question: 'Can I print or download the generated bill?',
              answer: 'Yes. These pages are designed for shareable, printable bill output and many include PDF generation.',
            },
            {
              question: 'Can I edit charges and tax details before finalizing the bill?',
              answer: 'Yes. You can usually update line items, service charges, taxes, and totals before creating the final output.',
            },
            {
              question: 'Should I confirm GST or commercial compliance separately?',
              answer: 'Yes. If the document is being used commercially, you should confirm applicable taxes and local compliance requirements before final use.',
            },
          ];
        case 'quotations':
          return [
            {
              question: `What is ${routeLabel} designed to create?`,
              answer: `${routeLabel} helps create a structured commercial document such as a bill, invoice, or quotation with organized item, tax, and total sections.`,
            },
            {
              question: 'Can I use this page for client-facing documents?',
              answer: 'Yes. These generators are intended to help you produce cleaner client-facing documents for sharing, printing, or saving as PDF.',
            },
            {
              question: 'Can I adjust tax, discount, and line item details?',
              answer: 'Yes. These tools are built so you can edit item details, totals, charges, and tax-related inputs before generating the final document.',
            },
            {
              question: 'Should I review legal and accounting details before sending?',
              answer: 'Yes. Always verify business identity, tax numbers, terms, and amounts before issuing commercial documents externally.',
            },
          ];
        default:
          return [
            {
              question: `What is ${routeLabel} designed for?`,
              answer: `${routeLabel} helps you generate a formatted bill, receipt, slip, or business document with structured details and downloadable output.`,
            },
            {
              question: 'Can I use these generators for printable documents?',
              answer: 'Yes. These pages are designed to help create readable, shareable, and printable documents, often with PDF download support.',
            },
            {
              question: 'Are my entered bill details stored permanently?',
              answer: 'No. paisapyara generators primarily run in your browser, which helps keep entered values private during use.',
            },
            {
              question: 'Should I check legal or tax compliance before final use?',
              answer: 'Yes. If the document is being used commercially or officially, confirm fields, taxes, and local compliance requirements before issuing it.',
            },
          ];
      }
    case 'interest':
      return [
        {
          question: `What does ${routeLabel} calculate?`,
          answer: `${routeLabel} helps you estimate interest growth, future value, or inflation impact based on the numbers you enter.`,
        },
        {
          question: 'Can I compare different rates and durations?',
          answer: 'Yes. Change rate, amount, or time period inputs to compare how each assumption changes the result.',
        },
        {
          question: 'Are the outputs exact future guarantees?',
          answer: 'No. These are mathematical estimates and should be treated as planning guidance, not guaranteed outcomes.',
        },
        {
          question: 'Is this useful for education and planning?',
          answer: 'Yes. These tools are useful for understanding how interest and inflation influence money over time.',
        },
      ];
    default:
      return [
        {
          question: `How do I use ${routeLabel} on paisapyara?`,
          answer: `Enter your values into ${routeLabel} and review the instant output shown on the page. Update one input at a time to compare scenarios more clearly.`,
        },
        {
          question: `Is ${routeLabel} free to use?`,
          answer: `Yes. ${routeLabel} is free to use on paisapyara without registration.`,
        },
        {
          question: 'Are my inputs stored on a server?',
          answer: 'No. paisapyara tools primarily run in your browser, which helps keep your inputs and generated outputs private during use.',
        },
        {
          question: 'Can I use paisapyara tools on mobile?',
          answer: 'Yes. paisapyara pages are designed to work on both desktop and mobile browsers so you can calculate and review results on any device.',
        },
      ];
  }
};

const buildFaqItems = (pathname) => {
  if (pathname === '/blog') {
    return [
      {
        question: 'What can I read on the paisapyara blog?',
        answer: 'The paisapyara blog covers budgeting, investing, taxes, retirement planning, loans, and practical money management topics for Indian users.',
      },
      {
        question: 'Are the blog articles beginner friendly?',
        answer: 'Yes. The articles are written in a practical style so beginners can understand the topic and then use the related calculators to apply it.',
      },
      {
        question: 'Can I use blog posts together with calculators?',
        answer: 'Yes. Many posts are linked to a relevant calculator or generator so you can move from concept to actual numbers quickly.',
      },
      {
        question: 'Is the paisapyara blog free to access?',
        answer: 'Yes. Blog content on paisapyara is free to read without registration.',
      },
    ];
  }

  if (pathname.startsWith('/blog/')) {
    return [
      {
        question: 'What is this article about?',
        answer: 'This article explains a focused personal finance topic and is meant to help you understand the decision before applying it with real numbers.',
      },
      {
        question: 'Can I use a calculator with this article?',
        answer: 'Yes. paisapyara articles are designed to work well with related calculators so you can test assumptions and compare outcomes.',
      },
      {
        question: 'Is the content beginner friendly?',
        answer: 'Yes. The article content is intended to be practical, readable, and useful for everyday money decisions.',
      },
      {
        question: 'Should I still verify important financial decisions?',
        answer: 'Yes. Use articles and calculators for planning, but verify major tax, loan, legal, or investment decisions with official sources or a qualified advisor.',
      },
    ];
  }

  if (ROUTE_FAQ_OVERRIDES[pathname]) {
    return ROUTE_FAQ_OVERRIDES[pathname];
  }

  const routeLabel = getRouteLabel(pathname);
  return buildCategoryFaqs(pathname, routeLabel);
};

const RouteFAQSection = () => {
  const { pathname } = useLocation();

  if (CUSTOM_FAQ_PATHS.includes(pathname)) {
    return null;
  }

  const faqItems = buildFaqItems(pathname);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          })}
        </script>
      </Helmet>
      <section className="info-box seo-section">
        <h4>Frequently Asked Questions</h4>
        <div className="seo-accordion-grid">
          {faqItems.map((item) => (
            <details key={item.question} className="seo-accordion-item">
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
};

export default RouteFAQSection;

